import React from "react";
import { toast as reactToastify } from "react-toastify";

/** Single toast id for API / global error deduplication (react-toastify replaces same id). */
export const API_ERROR_TOAST_ID = "api-error";

function renderContent(title, description) {
  if (description == null || description === "") {
    return title;
  }
  return React.createElement(
    "div",
    null,
    React.createElement(
      "p",
      { className: "font-semibold text-sm text-[var(--text-color)]" },
      title,
    ),
    React.createElement(
      "p",
      { className: "text-xs text-gray-500 mt-0.5 whitespace-pre-line" },
      description,
    ),
  );
}

export function showSuccess(title, description, options) {
  return reactToastify.success(renderContent(title, description), options);
}

export function showError(title, description, options) {
  return reactToastify.error(renderContent(title, description), options);
}

export function showWarning(title, description, options) {
  return reactToastify.warn(renderContent(title, description), options);
}

export function showInfo(title, description, options) {
  return reactToastify.info(renderContent(title, description), options);
}

export function showLoading(content, options) {
  return reactToastify.loading(content, options);
}

export function dismissToast(id) {
  reactToastify.dismiss(id);
}

/**
 * Mark an error so global handlers (unhandledrejection / window "error") skip duplicate toasts.
 */
export function markErrorToastShown(err) {
  if (err && typeof err === "object") err.__globalErrorToastShown = true;
}

/**
 * The AI backend (directly, or through the Node server) flags a used-up OpenAI token
 * allowance with this header, or a 402 + code when the call failed outright. Returns the
 * user-facing message, or null.
 */
const AI_QUOTA_HEADER = "x-ai-quota-exhausted";
const AI_QUOTA_ERROR_CODE = "ai_token_quota_exhausted";
const AI_QUOTA_TOAST_ID = "ai-quota";

export function getAiQuotaMessage(response) {
  const data = response?.data;
  return (
    response?.headers?.[AI_QUOTA_HEADER] ||
    (data?.code === AI_QUOTA_ERROR_CODE ? data.detail || data.error : null) ||
    null
  );
}

/** Response interceptor: a request that still succeeded can carry the quota warning. */
export function toastAiQuota(response) {
  const message = getAiQuotaMessage(response);
  // TEMP [quota-debug]: remove once the quota toast is confirmed.
  if (message) console.log("[quota-debug] toast", response?.status, response?.config?.url);
  if (message) showError(message, undefined, { toastId: AI_QUOTA_TOAST_ID });
  return response;
}

/** Request aborted on purpose (React Query cancel, AbortController): not a failure. */
export function isCanceledRequest(err) {
  return (
    err?.code === "ERR_CANCELED" ||
    err?.name === "CanceledError" ||
    err?.name === "AbortError"
  );
}

// A full navigation / reload aborts in-flight requests; those "network errors" are not real.
let pageIsLeaving = false;
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => {
    pageIsLeaving = true;
  });
  window.addEventListener("pageshow", () => {
    pageIsLeaving = false;
  });
}

/**
 * Reads (GET, or a POST marked `isRead`) are reported by the React Query cache once all
 * retries failed and nothing is on screen, not per attempt: a failed attempt that a retry
 * or the next poll recovers is invisible to the user and must not raise a toast.
 */
export function isReadRequest(config) {
  return config?.isRead === true || String(config?.method || "get").toLowerCase() === "get";
}

/**
 * The shared rule for the axios interceptors: toast a failed write (a user action the user
 * is waiting on) right away. Reads and aborted requests are left to the query cache.
 */
export function toastApiError(err, message) {
  // Shown for every request kind: the user has to act on it, and retries won't fix it.
  if (getAiQuotaMessage(err?.response)) {
    markErrorToastShown(err);
    toastAiQuota(err.response);
    return;
  }
  if (err?.config?.skipGlobalErrorToast) return; // the caller reports it
  if (isCanceledRequest(err) || (!err?.response && pageIsLeaving)) {
    markErrorToastShown(err); // nothing to tell the user, anywhere
    return;
  }
  if (isReadRequest(err?.config)) return; // reported once by the query cache
  markErrorToastShown(err);
  showError(message ?? getFriendlyApiErrorMessage(err), undefined, { toastId: API_ERROR_TOAST_ID });
}

/**
 * Query-cache reporting: a query that failed after its retries, on a screen with nothing
 * to show yet. Background refetches and polls keep their last data on screen, so their
 * failures stay silent; `meta: { silentError: true }` opts a query out (it shows its own state).
 */
export function toastQueryError(error, query) {
  if (error?.__globalErrorToastShown || isCanceledRequest(error)) return;
  if (query?.meta?.silentError || query?.state?.data !== undefined) return;
  if (!error?.response && pageIsLeaving) return;
  markErrorToastShown(error);
  // API helpers often rethrow as new Error("Failed to …"): that message is the useful one.
  const message = error?.isAxiosError ? getFriendlyApiErrorMessage(error) : error?.message;
  showError(message || "Something went wrong. Please try again.", undefined, {
    toastId: API_ERROR_TOAST_ID,
  });
}

/**
 * A read run as a mutation (e.g. an export): report it once the mutation's own onError
 * handlers had their turn, unless one of them already showed a toast.
 */
export function toastMutationReadError(error) {
  if (!error?.isAxiosError || !isReadRequest(error.config) || error.config?.skipGlobalErrorToast) return;
  setTimeout(() => {
    if (error.__globalErrorToastShown || isCanceledRequest(error) || pageIsLeaving) return;
    markErrorToastShown(error);
    showError(getFriendlyApiErrorMessage(error), undefined, { toastId: API_ERROR_TOAST_ID });
  }, 0);
}

/**
 * User-facing message for axios-style errors (network, 5xx, or server body).
 */
export function getFriendlyApiErrorMessage(
  err,
  fallback = "Something went wrong. Please try again.",
) {
  if (!err?.response) {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      return "You're offline. Check your internet connection.";
    }
    if (err?.code === "ECONNABORTED") {
      return "The server is taking too long to respond. Please try again.";
    }
    return "Can't reach the server. Check your connection.";
  }
  const quotaMessage = getAiQuotaMessage(err.response);
  if (quotaMessage) return quotaMessage;
  const status = err.response.status;
  if (status >= 500) {
    return "The server had a problem. Please try again in a moment.";
  }
  return (
    err.response?.data?.error ||
    err.response?.data?.message ||
    err.message ||
    fallback
  );
}

/**
 * Use in TanStack Query mutation/query `onError` when axios may have already shown a toast.
 * Skips when `error.__globalErrorToastShown` is set by an axios interceptor.
 */
export function showErrorFromMutation(error, fallbackMessage, options) {
  if (error?.__globalErrorToastShown) {
    return;
  }
  const toastOpts = { toastId: API_ERROR_TOAST_ID, ...options };
  if (error?.config?.skipGlobalErrorToast === true) {
    const msg =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      fallbackMessage;
    showError(msg, undefined, toastOpts);
    markErrorToastShown(error);
    return;
  }
  const msg =
    fallbackMessage ||
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    "Something went wrong. Please try again.";
  showError(msg, undefined, toastOpts);
  markErrorToastShown(error);
}

/** Raw react-toastify API for custom JSX / advanced options */
export const toast = reactToastify;
